import React from 'react';
import moment from 'moment';
import type {
  CalendarComponentEvent,
  TimeZoneIana,
  CalendarPermissions,
} from '@axiom/validation';

import { Layout } from '../../layout/Layout/Layout';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { Gutter } from '../../layout/Gutter/Gutter';
import { Paragraph } from '../../content/Paragraph/Paragraph';
import { ParagraphHeader } from '../../content/ParagraphHeader/ParagraphHeader';
import { DateUtil } from '../../../utils/date-util';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { CalendarTimeZoneUtil } from '../../../utils/calendar-time-zone-util';
import { Modal } from '../../element/Modal/Modal';
import { ModalSection } from '../../element/Modal/ModalSection';
import { Icon } from '../../element/Icon/Icon';
import { ModalFooter } from '../../element/Modal/ModalFooter';
import { Button } from '../../element/Button/Button';
import { ModalHeader } from '../../element/Modal/ModalHeader';

import { EVENT_STATES } from './calendar-events.const';

export type CalendarDeleteModalProps = {
  name?: string;
  permissions: CalendarPermissions;
  onClose: () => void;
  deleteEvent: CalendarComponentEvent;
  onDelete: () => void;
  setConfirmOpen: (arg: boolean) => void;
  // Suppress the Delete action regardless of permissions. Used by the
  // single-event edit flow (RawCalendar with editableEventId set) so that
  // clicking on any event opens the details modal in read-only form.
  hideDelete?: boolean;
};

export const CalendarDeleteModal = ({
  name = 'CALENDAR_DELETE_MODAL',
  permissions,
  onClose,
  deleteEvent,
  onDelete,
  setConfirmOpen,
  hideDelete = false,
}: CalendarDeleteModalProps) => {
  const { isMobile } = useBreakpoint();

  const timezone = CalendarTimeZoneUtil.getZoneFromMomentZone(
    moment.tz(new Date(), moment(deleteEvent.start).tz() as TimeZoneIana)
  );
  return (
    <Modal name={name}>
      <ModalHeader
        onClose={() => {
          onClose();
        }}
        name="DELETE_MODAL_HEADER"
      >
        {deleteEvent.busy ? 'Interview' : 'Availability'} Details
      </ModalHeader>
      <ModalSection>
        {!!deleteEvent.accountName && (
          <>
            <ParagraphHeader name="DELETE_MODAL_ACCOUNT_NAME">
              {deleteEvent.accountName}
            </ParagraphHeader>
            <Gutter bottom="16px" />
          </>
        )}
        {(deleteEvent.attendees?.length ?? 0) > 0 && (
          <>
            <Layout position="top" horizontalGutter="8px">
              <LayoutItem topGutter="4px">
                <Icon name="avatar" />
              </LayoutItem>
              <LayoutItem>
                {deleteEvent.attendees?.map((attendee, index) => (
                  <Paragraph
                    name="ATTENDEE"
                    key={`${attendee.email}_${index + 1}`}
                  >
                    {`${attendee.name} (${attendee.email})`}
                  </Paragraph>
                ))}
              </LayoutItem>
            </Layout>
            <Gutter vertical="8px" />
          </>
        )}
        <Layout position="middle" horizontalGutter="8px">
          <Icon name="calendar" />
          <Paragraph name="EVENT_DATE">{`${DateUtil.displayLongDay(
            deleteEvent.start
          )}, ${DateUtil.displayLongMonthDayYear(
            deleteEvent.start
          )}`}</Paragraph>
        </Layout>
        <Gutter vertical="8px" />
        <Layout position="middle" horizontalGutter="8px">
          <Icon name="clock" />
          <Paragraph name="EVENT_TIME">{`${DateUtil.displayFullTimeRange(
            deleteEvent.start,
            deleteEvent.end
          )} ${timezone?.abbr}`}</Paragraph>
        </Layout>
      </ModalSection>
      <ModalFooter>
        {!isMobile &&
        !hideDelete &&
        ((!deleteEvent.busy && permissions.eventBoundary.canDelete) ||
          (deleteEvent.busy &&
            deleteEvent.state !== EVENT_STATES.SAVED &&
            permissions.staticEvent.canDelete) ||
          (deleteEvent.busy &&
            deleteEvent.state === EVENT_STATES.SAVED &&
            permissions.staticEvent.canDeleteSaved)) ? (
          <Button
            name="DELETE_BUTTON"
            variation="outline"
            onClick={() => {
              if (
                deleteEvent.busy &&
                (deleteEvent.attendees?.length ?? 0) > 0
              ) {
                setConfirmOpen(true);
              } else {
                onDelete();
              }
            }}
          >
            Delete
          </Button>
        ) : null}
        <Button
          name="CLOSE_BUTTON"
          onClick={() => {
            onClose();
          }}
        >
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
